/**
 * What a scan's status means to the console: is it still running, and what does
 * the run button say while it is.
 *
 * The four in-flight statuses are `IN_FLIGHT_SCAN_STATUSES` in
 * `@driftwatch/contracts` restated, not a second definition. That constant is
 * what the scheduler skips a tenant on (`scan_in_flight`, "Scan already
 * running") and what the manual run route refuses a second scan on. The console
 * may import nothing internal — see the layer table in CLAUDE.md — so, like
 * every type in `dto.ts`, this is a hand-written mirror.
 *
 * Keep it in step with that constant. Drifting apart does not fail anything: it
 * makes the button offer a second read of a customer's tenant that the API then
 * refuses with a 409 the operator has no way to have predicted.
 *
 * `interrupted` is absent on purpose, and it is the entry most likely to be
 * added here by mistake, because the word sounds like something still in
 * progress. It is terminal — what reconciliation stamps on a row whose worker
 * was killed — so polling must stop on it and the run button must come back.
 * Listing it would leave the console waiting on a scan nothing will ever write
 * to again, which is the failure ADR-0035 exists to remove.
 */
const IN_FLIGHT: ReadonlySet<string> = new Set([
  'queued',
  'collecting',
  'evaluating',
  'narrating',
]);

export const isInFlight = (status: string): boolean => IN_FLIGHT.has(status);

/**
 * The stage a running scan is at, in the operator's words.
 *
 * A real scan takes about three quarters of a minute and spends nearly all of
 * it in collection, so one unchanging "Running…" would look stuck. Naming the
 * stage is the only evidence the operator gets that anything is still moving.
 */
const STAGE: Readonly<Record<string, string>> = {
  queued: 'Queued…',
  collecting: 'Collecting…',
  evaluating: 'Evaluating…',
  narrating: 'Writing narrative…',
};

/** What the button says when this client has nothing under way. */
export const IDLE_LABEL = 'Run assessment';

/**
 * `null` is the window between the click and the first poll that sees a row:
 * the API answers 202 as soon as the job is queued, and the row is written by
 * the worker that picks it up, so for a moment there is genuinely no status to
 * report. The fallback covers a status the server grows that this console has
 * not been taught yet — a vague running label on a button that must not be
 * pressed is safer than an idle one.
 */
export const stageLabel = (status: string | null): string =>
  status === null ? 'Starting…' : (STAGE[status] ?? 'Running…');
